import { Event } from '@types'
import { MappedEvent, NumberedMappedEvent, UnmappedEvent } from './types'

export interface SplitEvents {
	mapped: MappedEvent[]
	unmapped: UnmappedEvent[]
}

const sortEvents = (a: { event: Event }, b: { event: Event }): number => {
	if (a.event.tripDayIndex !== b.event.tripDayIndex) return a.event.tripDayIndex - b.event.tripDayIndex
	return a.event.eventIndex - b.event.eventIndex
}

export const splitEventsByGeometry = (events: Event[] | undefined): SplitEvents => {
	const mapped: MappedEvent[] = []
	const unmapped: UnmappedEvent[] = []

	if (!events) return { mapped, unmapped }

	for (const event of events) {
		const geometry = event.location?.placeDetails?.geometry?.location
		if (geometry && typeof geometry.lat === 'number' && typeof geometry.lng === 'number') {
			mapped.push({ event, dayIndex: event.tripDayIndex, lat: geometry.lat, lng: geometry.lng })
		} else {
			unmapped.push({ event, dayIndex: event.tripDayIndex })
		}
	}

	mapped.sort(sortEvents)
	unmapped.sort(sortEvents)
	return { mapped, unmapped }
}

export const numberMappedEvents = (mapped: MappedEvent[]): NumberedMappedEvent[] =>
	mapped.map((item, idx) => ({ ...item, order: idx + 1 }))

export const formatEventTime = (startTime: string | null | undefined): string | null => {
	if (!startTime) return null
	const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(startTime)
	if (!match) return null
	const hours = parseInt(match[1], 10)
	const minutes = match[2]
	const period = hours >= 12 ? 'PM' : 'AM'
	const displayHour = hours % 12 === 0 ? 12 : hours % 12
	return `${displayHour}:${minutes} ${period}`
}

/** Every day that has at least one pin, in day order — the map's default filter shows all of them. */
export const daysWithPins = (mapped: MappedEvent[]): Set<number> => new Set(mapped.map(m => m.dayIndex))

/**
 * The filter the map opens with: the single day named by the `?day=` param
 * (1-based) when that day has a pin, otherwise every day with pins.
 */
export const initialSelectedDays = (mapped: MappedEvent[], dayParam: string | null): Set<number> => {
	const withPins = daysWithPins(mapped)
	if (dayParam !== null && /^\d+$/.test(dayParam)) {
		const dayIndex = Number(dayParam) - 1
		if (withPins.has(dayIndex)) return new Set([dayIndex])
	}
	return withPins
}
